/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: Global view of design tokens categorized by type or layer, with theme management
 * Style referenceFiles:
 * Design for: Desktop
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - Toggle Token view: By Type (Color, Font, etc.) vs By Layer (Primitive, Semantic, Component)
 * - Manage Themes (Create, Switch)
 * - Open Token Relationship Graph
 * - Import/Export Tokens
 * - Navigate to: TokenCategoryDetail
 *
 * ## Basic Layout
 * Column split: Sidebar + Main
 *
 * ## Reuse Components
 * - `LayoutSidebar`
 *
 * ## Page Layout
 * `flex` Column split: Sidebar + Content `flex-1`
 *
 * **Sidebar**: Reuse `LayoutSidebar`, activeKey="tokens"
 *
 * **Main Content** `flex flex-col h-screen overflow-hidden`:
 * - **Header** `flex items-center justify-between`: Title "Design Tokens" + Right actions (Theme Selector Dropdown, [Graph View], [Import/Export])
 * - **Tabs** `flex`: "By Type" | "By Layer"
 * - **Grid Content** `flex-1 overflow-auto grid grid-cols-3`:
 *   - Category Cards `flex flex-col` (Text-only): Title (e.g., "Colors", "Typography"), Token count, Preview swatches/examples. Click navigates to TokenCategoryDetail.
 *
 * ## Mock Data
 * Themes: Default, Dark Mode, High Contrast.
 * Categories (Type view): Colors (42 tokens), Typography (12 tokens), Spacing (8 tokens), Shadows (4 tokens).
 *
 * ## Reference
 * - canvas/[Feature] DesignToken/feature-brief.md
 * </page-design>
 */

import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { LayoutSidebar } from '@/components/function/LayoutSidebar'
import * as TokenOverviewStore from '@/stores/TokenOverviewStore'
import { pageLinks } from '@/pageLinks'

const typeIcons: Record<string, string> = {
  color: 'lucide:palette',
  typography: 'lucide:type',
  spacing: 'lucide:ruler',
  shadow: 'lucide:layers',
  border: 'lucide:square',
  radius: 'lucide:circle',
}
const typeLabels: Record<string, string> = {
  color: '颜色',
  typography: '排版',
  spacing: '间距',
  shadow: '阴影',
  border: '边框',
  radius: '圆角',
}
const layerLabels: Record<string, string> = {
  primitive: '基础层',
  semantic: '语义层',
  component: '组件层',
}
const layerIcons: Record<string, string> = {
  primitive: 'lucide:atom',
  semantic: 'lucide:tag',
  component: 'lucide:component',
}

function CreateThemeModal({ onClose }: { onClose: () => void }) {
  const createTheme = useSetAtom(TokenOverviewStore.createThemeAtom)
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await createTheme({ name, description: desc || undefined })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.1)] w-[440px] p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-6">创建主题</h2>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">主题名称 *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="例如：Dark Mode" className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">描述</label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="主题的用途说明..." rows={3} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15 resize-none" />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 transition-all">取消</button>
          <button onClick={handleSubmit} disabled={submitting || !name.trim()} className="bg-[#1D263A] hover:bg-[#151D2E] disabled:opacity-50 text-white font-medium py-2.5 px-5 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] transition-all text-sm">{submitting ? '创建中...' : '创建主题'}</button>
        </div>
      </div>
    </div>
  )
}

function CategoryGrid() {
  const { data, isPending } = useAtomValue(TokenOverviewStore.refreshAtom)
  const viewMode = useAtomValue(TokenOverviewStore.viewModeAtom)

  if (isPending) {
    return (
      <div className="grid grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-white rounded-2xl animate-pulse" />)}
      </div>
    )
  }

  if (!data) return null

  const items = viewMode === 'type' ? data.tokensByType : data.tokensByLayer
  const labels = viewMode === 'type' ? typeLabels : layerLabels
  const icons = viewMode === 'type' ? typeIcons : layerIcons

  if (items.length === 0) {
    return <div className="text-center py-20 text-gray-400"><Icon icon="lucide:palette" className="text-5xl mb-4 mx-auto" /><p>暂无 Token 数据</p></div>
  }

  const colorTokens = viewMode === 'type' ? data.tokens.filter(t => t.type === 'color').slice(0, 6) : []

  return (
    <div className="grid grid-cols-3 gap-6">
      {items.map(item => {
        const key = viewMode === 'type' ? (item as { type: string }).type : (item as { layer: string }).layer
        const count = item.count
        const label = labels[key] || key
        const icon = icons[key] || 'lucide:box'
        const isColor = viewMode === 'type' && key === 'color'
        const swatches = isColor ? colorTokens : []

        return (
          <Link key={key} to={viewMode === 'type' ? pageLinks.TokenCategoryDetail(key) : pageLinks.TokenCategoryDetail(key)} className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 group">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#1D263A]/5 flex items-center justify-center">
                <Icon icon={icon} className="text-xl text-[#1D263A]" />
              </div>
              <div>
                <h3 className="font-bold">{label}</h3>
                <span className="text-xs text-gray-500">{count} 个 Token</span>
              </div>
            </div>
            {isColor && swatches.length > 0 && (
              <div className="flex gap-2 mt-2">
                {swatches.map(t => (
                  <div key={t.id} className="w-8 h-8 rounded-lg shadow-inner border border-gray-100" style={{ backgroundColor: t.value }} title={`${t.name}: ${t.value}`} />
                ))}
              </div>
            )}
            {!isColor && (
              <div className="mt-2 text-xs text-gray-400">
                {viewMode === 'type' ? `查看所有${label} Token` : `${label}中的所有 Token`}
              </div>
            )}
          </Link>
        )
      })}
    </div>
  )
}

export default function TokenOverview() {
  const { data } = useAtomValue(TokenOverviewStore.refreshAtom)
  const viewMode = useAtomValue(TokenOverviewStore.viewModeAtom)
  const setViewMode = useSetAtom(TokenOverviewStore.viewModeAtom)
  const selectedThemeId = useAtomValue(TokenOverviewStore.selectedThemeIdAtom)
  const setSelectedThemeId = useSetAtom(TokenOverviewStore.selectedThemeIdAtom)
  const [showCreateTheme, setShowCreateTheme] = useState(false)

  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25] overflow-hidden">
      <LayoutSidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden p-6 pb-0">
        <header className="flex items-center justify-between mb-6 px-2">
          <h1 className="text-2xl font-bold tracking-tight">Design Tokens</h1>
          <div className="flex items-center gap-3">
            <select value={selectedThemeId} onChange={e => setSelectedThemeId(e.target.value)} className="px-4 py-2.5 bg-white rounded-xl text-sm outline-none shadow-[0_2px_10px_rgb(0,0,0,0.02)] border border-transparent focus:border-[#1D263A]/10 transition-all text-gray-700">
              <option value="">全部主题</option>
              {data?.themes.map(t => <option key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (默认)' : ''}</option>)}
            </select>
            <button onClick={() => setShowCreateTheme(true)} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all flex items-center gap-2">
              <Icon icon="lucide:plus" />新建主题
            </button>
            <button className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all flex items-center gap-2">
              <Icon icon="lucide:git-branch" />关系图
            </button>
            <button className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all flex items-center gap-2">
              <Icon icon="lucide:download" />导入/导出
            </button>
          </div>
        </header>

        <div className="flex gap-1 mb-6 px-2">
          <button onClick={() => setViewMode('type')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${viewMode === 'type' ? 'bg-white text-[#1D263A] shadow-[0_2px_10px_rgb(0,0,0,0.04)]' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}>
            <Icon icon="lucide:grid-3x3" className="text-lg" />按类型
          </button>
          <button onClick={() => setViewMode('layer')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${viewMode === 'layer' ? 'bg-white text-[#1D263A] shadow-[0_2px_10px_rgb(0,0,0,0.04)]' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}>
            <Icon icon="lucide:layers" className="text-lg" />按层级
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pb-8 px-2">
          <CategoryGrid />
        </div>
      </div>

      {showCreateTheme && <CreateThemeModal onClose={() => setShowCreateTheme(false)} />}
    </div>
  )
}
